import { CategoryType } from '@model/Category';
import { SalesmanType } from '@model/Salesman';
import { trim } from 'lodash';
import * as Yup from 'yup';
import { LocationType } from './Location';
import { Model, ModelClass, ModelClassType } from './Model';
import { UserType, UserValidation } from './User';

export interface CustomerType extends ModelClass {
    country_calling_code: any;
    id: number;
    name: string;
    email: string;
    user_id: number;
    salesman_id: number;
    user: UserType;
    location: LocationType;
    organization_name: string;
    active_orders: number;
    latest_order_status: string;
    shipping_locations: any;
    salesman_name: string;
    salesman_email: string;
    salesman_phone: string;
    lakeshore_id: string;
    salesmen_commission_type: number;
    category: CategoryType;
    paid_orders: number;
    category_id: number;
    credit: number;
    wallet_activity: string[];
    is_wholesaler: boolean;
    type: string;
    is_vip: boolean;
    has_free_shipping: boolean;
    salesman_assigned_email_opened: boolean;
    salesman_assigned_at: Date;
    last_order_paid_at: Date;
    salesman: SalesmanType;
    notes: any;
    what_sports: any;
    wholesaler: any;
    sap_number: string;
    what_role: string;
    authorized_to_order: boolean;
    instagram_handle: string;
    number_of_players: string;
    ordering_stage: string;
    website: string;
    additional_information: string;
    has_current_supplier: boolean;
    current_supplier: string;
    supplier_switch_reasons: string;
    source: string;

    findOrCreate(user: UserType): Promise<CustomerType>;

    toggleWholesaler(checked?: boolean): Promise<{ message: string }>;

    toggleVip(checked?: boolean): Promise<{ message: string }>;

    toggleFreeShipping(checked?: boolean): Promise<{ message: string }>;
}

export const Customer = Model<CustomerType>('Customer', { endpoint: 'apparel-customers' })`
    id: Int;
    user_id: Int;
    name: String;
    email: String;
    salesman_id: Int;
    user: Any;
    location: Any;
    organization_name: String;
    active_orders: Int;
    latest_order_status: String;
    salesman_name: String;
    salesman_email: String;
    salesman_phone: String;
    lakeshore_id: String;
    salesmen_commission_type: Int;
    shipping_locations: Any;
    category:Any;
    is_wholesaler: boolean;
    type: string;
    is_vip: boolean;
    has_free_shipping: boolean;
    category_id: Int;
    credit: Float;
    wallet_activity: Any;
    approximate_number_pieces: any;
    spend_per_year:any;
    kind_of_organization:any;
    what_sports:any;
    notes:any;
    wholesaler:Any;
    sap_number: String;
    what_role: String;
    authorized_to_order: Any;
    instagram_handle: String;
    number_of_players: String;
    ordering_stage: String;
    website: String;
    additional_information: String;
    has_current_supplier: Any;
    current_supplier: String;
    supplier_switch_reasons: String;
    source: String;
`;

Customer.$define('toggleWholesaler', (self: ModelClassType) =>
    self.http$.post('apparel-customers/toggle-wholesaler', {
        customer_id: self.id,
    }),
);

Customer.$define('toggleVip', (self: ModelClassType) =>
    self.http$.post('apparel-customers/toggle-vip', {
        customer_id: self.id,
    }),
);

Customer.$define('toggleFreeShipping', (self: ModelClassType) =>
    self.http$.post('apparel-customers/toggle-free-shipping', {
        customer_id: self.id,
    }),
);

Customer.findOrCreate = async (user: UserType): Promise<CustomerType> => {
    const self = new Customer();
    const customerId = user.get('customer_id');
    let data$: null | CustomerType = null;

    try {
        if (customerId) {
            const { data } = await Customer.find(customerId);
            data$ = data;
        }
    } catch (e) {
        // create
        data$ = null;
    }

    if (!data$) {
        const { data } = await Customer.create({
            user: {
                email: user.get('email'),
                phone: user.get('phone'),
            },
        });

        data$ = data;
    }

    return self.$fill(data$);
};

/**
 * render Full name of user.
 * @param customer
 * @returns {*}
 */
export const renderFirstAndLastName = (customer) =>
    trim(`${customer.user.first_name || ''} ${customer.user.last_name || ''}`);

export const CustomerValidation = Yup.object({
    user: UserValidation,

    country: Yup.object({
        id: Yup.number().required('Id is required'),
        name: Yup.string().required('Name is required'),
    }),

    state: Yup.string().required('State is Required!'),

    city: Yup.string().required('City is Required!'),
    sport: Yup.string().required('Sport is Required!'),
    sport_other: Yup.string().when('sport', {
        is: 'Other',
        then: Yup.string().required('Mention Other Sport'),
        otherwise: Yup.string().notRequired(),
    }),
    what_role: Yup.string().required('Field is Required!'),
    what_role_other: Yup.string().when('what_role', {
        is: 'Other',
        then: Yup.string().required('Field is Required!'),
        otherwise: Yup.string().notRequired(),
    }),
    authorized_to_order: Yup.mixed().oneOf([true, false], 'Field is Required!').required('Field is Required!'),
    instagram_handle: Yup.string(),
    lakeshore_id: Yup.string(),
});

export const CreateCustomerValidation = Yup.object({
    first_name: Yup.string().required('Field is required!'),
    user: Yup.object({
        email: Yup.string().required('Field is required!').email('E-mail is invalid!').max(255, 'E-mail is too big!'),
    }),
});

export const CustomerRegistrationValidation = Yup.object({
    organization_name: Yup.string().required('The Company or Team-Name is required'),

    user: Yup.object({
        first_name: Yup.string().required('First Name is required'),
        last_name: Yup.string().required('Last Name is required'),
        email: Yup.string().required('E-Mail is required').email('E-Mail is invalid'),
        password: Yup.string().required('Password is required'),
    }),
});
